import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type { Policy, PolicyListResponse, PolicyCreateRequest, PolicyUpdateRequest } from './types'

export const policyKeys = {
  all: ['policies'] as const,
  list: () => [...policyKeys.all, 'list'] as const,
}

export const usePolicies = () => {
  return useQuery({
    queryKey: policyKeys.list(),
    queryFn: async () => {
      const response = await api.get<PolicyListResponse>('/policies')
      return response
    },
    staleTime: 2 * 60 * 1000,
  })
}

export const useCreatePolicy = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: PolicyCreateRequest) => {
      return api.post<Policy>('/policies', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyKeys.all })
    },
  })
}

export const useUpdatePolicy = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: PolicyUpdateRequest }) => {
      return api.put<Policy>(`/policies/${id}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyKeys.all })
    },
  })
}

export const useDeletePolicy = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      return api.delete(`/policies/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyKeys.all })
    },
  })
}

export const useTogglePolicy = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      return api.patch(`/policies/${id}/toggle`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyKeys.all })
    },
  })
}
